/**
 * Shared portfolio-scoping helper for Supabase queries.
 *
 * Three states:
 *   undefined  => "All Portfolios" — no property filter applied.
 *                 Cross-agent isolation is handled by owner_id/owner_id.
 *   []         => "Empty portfolio" — match NOTHING (query returns zero rows).
 *   ['id',...] => "Specific portfolio" — filter to those property IDs.
 *
 * Usage:
 *   query = applyPropertyFilter(query, propertyIds)
 *   query = applyPropertyFilter(query, propertyIds, 'id')  // properties table
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SupabaseQuery = { in: (column: string, values: string[]) => any }

export function applyPropertyFilter<Q extends SupabaseQuery>(
  query: Q,
  propertyIds: string[] | undefined,
  column = 'property_id',
): Q {
  if (propertyIds === undefined) return query
  // Empty array: .in(col, []) produces PostgREST `col=in.()` which matches nothing
  return query.in(column, propertyIds)
}

/**
 * Convert propertyIds for RPC parameters that use the
 * `p_property_ids IS NULL OR col = ANY(p_property_ids)` convention.
 *
 *   undefined  => null  (IS NULL => no filter)
 *   []         => []    (ANY('{}') => matches nothing)
 *   ['id',...] => ['id',...] (ANY('{id,...}') => matches those IDs)
 */
export function propertyIdsForRpc(propertyIds: string[] | undefined): string[] | null {
  if (propertyIds === undefined) return null
  return propertyIds
}
